import * as React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import * as Device from 'expo-device';

import { Text } from '../Text';
import { Input } from '../Input';
import { spacing } from '../../theme';
import { HelpMarkIcon, CloseIcon as RemoveIcon } from '../../assets';
import { useEditableItems } from './useEditableItems';
import { withTranslation } from '../../locales/hoc';
import { HoverTextTooltip } from '../HoverTextTooltip';

const EditableItemsComponent = ({ Icon, title, helpText, items, onChange }: EditableItemsProps) => {
	const { t } = useTranslation();

	const { handleChange, handleAdd, handleRemove } = useEditableItems(items, onChange);

	return (
		<View style={styles.root}>
			<View style={styles.header}>
				<Icon style={iconStyle} />
				<Text category="h5">{title}</Text>
				{helpText && (
					<HoverTextTooltip
						content={helpText}
						position={Device.deviceType === Device.DeviceType.DESKTOP ? 'right' : 'bottom'}
						wrapperStyle={styles.tooltipWrapper}
						style={styles.tooltip}
					>
						<HelpMarkIcon />
					</HoverTextTooltip>
				)}
			</View>

			<View style={styles.items}>
				{items.map((item, index) => (
					<View key={index} style={styles.item}>
						<Input
							style={styles.input}
							multiline
							value={item}
							onChangeText={(text) => handleChange(index, text)}
						/>
						<TouchableOpacity style={styles.removeButton} onPress={() => handleRemove(index)}>
							<RemoveIcon />
						</TouchableOpacity>
					</View>
				))}
			</View>

			<TouchableOpacity style={styles.addButton} onPress={handleAdd}>
				<Text category="s1">+ {t('editable-items.add-information')}</Text>
			</TouchableOpacity>
		</View>
	);
};

export const EditableItems = withTranslation(EditableItemsComponent);

export interface EditableItemsProps {
	Icon: React.FC<SvgProps>;
	title: string;
	helpText?: string;
	items: string[];
	onChange?: (items: string[]) => void;
}

const iconStyle = {
	marginRight: spacing(),
};
const styles = StyleSheet.create({
	root: {
		marginVertical: spacing(),
		flexDirection: 'column',
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		marginBottom: spacing(),
	},
	items: {
		zIndex: -1,
	},
	item: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginVertical: spacing(),
	},
	input: {
		width: '95%',
	},
	removeButton: {
		paddingLeft: spacing(),
		marginRight: 80,
	},
	addButton: {
		width: '100%',
		paddingVertical: spacing(),
		marginVertical: spacing(),
	},
	tooltipWrapper: { marginBottom: 6, marginHorizontal: spacing() },
	tooltip: { minWidth: 350 },
});
